'use client';

import Card from '@mui/material/Card';
import Typography from '@mui/material/Typography';
import Image from 'next/image';
import type { SxProps, Theme } from '@mui/material/styles';
import Stack from '@mui/material/Stack';
import PortableText from '@/components/PortableText';
import { PortableTextBlock } from '@portabletext/react';
interface RectangleCardProps {
  image: string;
  title: string;
  description: PortableTextBlock[];
  sx?: SxProps<Theme>;
}

export default function RectangleCard({ image, title, description, sx = {} }: RectangleCardProps) {
  const cardSx: SxProps<Theme> = {
    position: 'relative',
    borderRadius: 3,
    border: '1px solid',
    borderColor: 'divider',
    overflow: 'hidden',
    width: '100%',
    height: '100%',
    aspectRatio: '2/3',
    p: 0,
    transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
    '& img': {
      transition: 'transform 0.5s cubic-bezier(0.4, 0, 0.2, 1)',
    },
    '&:hover': {
      transform: 'translateY(-2px)',
      boxShadow: '0 8px 24px rgba(0, 0, 0, 0.15)',
    },
    '&:hover img': {
      transform: 'scale(1.04)',
    },
    '@media (prefers-reduced-motion: reduce)': {
      transition: 'none',
      '& img': { transition: 'none' },
      '&:hover': { transform: 'none' },
      '&:hover img': { transform: 'none' },
    },
    ...sx,
  };

  return (
    <div>
      <Card sx={cardSx}>
        {/* Background Image */}
        {image && (
          <Image
            src={image}
            alt={title}
            fill
            sizes='(max-width: 600px) 100vw, (max-width: 1024px) 50vw, 33vw'
            style={{ objectFit: 'cover' }}
          />
        )}
      </Card>
      {/* Content Overlay */}
      <Stack spacing={0.5} direction='column' sx={{ pt: 1 }}>
        <Typography
          variant='h5'
          component='h3'
          sx={{
            color: 'text.primary',
            fontWeight: 'bold',
            fontSize: { xs: '1.25rem', sm: '1.5rem' },
            lineHeight: 1.2,
          }}
        >
          {title}
        </Typography>
        <Typography
          variant='body2'
          component='div'
          sx={{
            color: 'text.primary',
            fontSize: { xs: '0.875rem', sm: '1rem' },
            lineHeight: 1.4,
            opacity: 0.9,
          }}
        >
          <PortableText blocks={description} />
        </Typography>
      </Stack>
    </div>
  );
}
